<template>
    <div
        class="my-6 mb-4 flex h-auto w-full flex-col items-start justify-start space-y-4"
    >
        <div class="flex h-auto w-full items-center justify-between">
            <p class="text-sm font-medium">Tax Details</p>
            <button
                type="button"
                class="bg-primary-accent-600 h-8 w-8 rounded-md text-white"
                @click="addTaxDetail"
            >
                <fa icon="fa-plus" />
            </button>
        </div>
        <div
            v-for="(detail, index) in taxDetails"
            :key="index"
            :class="[
                'flex h-auto w-full flex-col items-start justify-start space-y-4 rounded-md bg-slate-100 p-4 duration-150',
                opened === index ? 'h-auto' : 'h-20',
            ]"
        >
            <div class="flex h-auto w-full items-center justify-between">
                <p class="font-medium">
                    {{
                        detail.name && detail.name.length > 0
                            ? detail.name
                            : 'Detail ' + (index + 1)
                    }}
                </p>
                <div class="flex w-auto items-center justify-center space-x-2">
                    <button
                        type="button"
                        class="bg-danger-600 h-8 w-8 rounded-md text-white disabled:cursor-not-allowed disabled:opacity-80"
                        :disabled="taxDetails.length === 1"
                        @click="removeTaxDetail(index)"
                    >
                        <fa icon="fas fa-trash-alt" />
                    </button>
                    <button
                        type="button"
                        class="bg-primary-accent-600 h-8 w-8 rounded-md text-white"
                        @click="toggleTaxDetail(index)"
                    >
                        <fa icon="fa-chevron-down" />
                    </button>
                </div>
            </div>
            <div
                :class="[
                    'h-auto w-full flex-col items-start justify-start space-y-2',
                    opened === index ? 'flex' : 'hidden',
                ]"
            >
                <div
                    class="flex h-auto w-full flex-col items-start justify-start space-y-2"
                >
                    <p class="text-sm font-medium">{{ $t('name') }}</p>
                    <input
                        v-model="detail.name"
                        type="text"
                        placeholder="Name"
                        class="focus:border-primary-accent-500 h-10 w-full rounded-md border border-gray-500 focus:outline-none focus:ring-0"
                    />
                </div>
                <div
                    class="flex h-auto w-full flex-col items-start justify-start space-y-2"
                >
                    <p class="text-sm font-medium">{{ $t('rate') }}</p>
                    <div
                        class="relative flex h-auto w-full items-center justify-end px-0.5"
                    >
                        <input
                            v-model="detail.rate"
                            readonly
                            :placeholder="$t('rate')"
                            type="text"
                            class="focus:border-primary-accent-500 h-10 w-full rounded-md border border-gray-500 focus:outline-none focus:ring-0"
                        />
                        <EditButton
                            class="absolute right-1"
                            @click="
                                openFormulaModal({
                                    type: 'tax_detail_rate',
                                    name: detail.name,
                                })
                            "
                        />
                    </div>
                </div>
                <div
                    class="flex h-auto w-full flex-col items-start justify-start space-y-2"
                >
                    <p class="text-sm font-medium">{{ $t('amount') }}</p>
                    <div
                        class="relative flex h-auto w-full items-center justify-end px-0.5"
                    >
                        <input
                            v-model="detail.amount"
                            readonly
                            :placeholder="$t('amount')"
                            type="text"
                            class="focus:border-primary-accent-500 h-10 w-full rounded-md border border-gray-500 focus:outline-none focus:ring-0"
                        />
                        <EditButton
                            class="absolute right-1"
                            @click="
                                openFormulaModal({
                                    type: 'tax_detail_amount',
                                    name: detail.name,
                                })
                            "
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useTaxesStore } from '@src/utils/stores/taxes'
import { useToastStore } from '@src/utils/stores/toast'
import { storeToRefs } from 'pinia'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import EditButton from '../actions/EditButton.vue'

defineProps<{
    taxDetails?: { name: string; amount: string; rate: string }[]
}>()

const emit = defineEmits(['openFormulaModal'])

const toast = useToastStore()
const i18n = useI18n()
const taxStore = useTaxesStore()
const { taxDetails } = storeToRefs(taxStore)

const opened = ref<number | null>(0)

const addTaxDetail = () => {
    taxDetails.value.push({
        name: '',
        amount: '',
        rate: '',
    })
}

const removeTaxDetail = (index: number) => {
    taxDetails.value = taxDetails.value.filter(
        (_, detailIndex) => detailIndex !== index
    )
}

const toggleTaxDetail = (index: number) => {
    opened.value = opened.value === index ? null : index
}

const openFormulaModal = (detail: {
    type: 'tax_detail_amount' | 'tax_detail_rate'
    name: string
}) => {
    if (detail.name.length > 0) {
        emit('openFormulaModal', {
            type: detail.type,
            name: detail.name,
        })
    } else {
        toast.error(i18n.t('tax_detail_name_required'))
    }
}
</script>
